import type { FileEntry } from '@/lib/api/generated/model';
import type { KindLabel, Previewability } from './types';

/**
 * The single place this app decides what a file *is*.
 *
 * Two questions are answered from one table: the readable Kind shown in the
 * listing, and how the preview pane should render the file. They are derived
 * together on purpose (spec 059 FR-005) — two independent tables would drift,
 * and the day one called a `.md` file Markdown while the other called it Text,
 * nothing would fail. The page would simply contradict itself in one of the two
 * places, which is the kind of bug that survives for months.
 *
 * **Extension first, `content_type` as fallback.** `content_type` is whatever a
 * client declared at upload time and is nullable besides, while the extension is
 * the more reliable signal for how to treat a file. Note this is the opposite of
 * FR-002's literal wording; FR-005 is the stronger requirement and the existing
 * preview classifier already worked this way, so the precedence is preserved and
 * the divergence is recorded in research.md R1.
 */
interface Classification {
  label: KindLabel;
  previewability: Previewability;
}

const TEXT: Previewability = { kind: 'text' };
const IMAGE: Previewability = { kind: 'image' };
/** Shown in the browser's own PDF viewer (2026-10-05); it was a download only. */
const PDF: Previewability = { kind: 'pdf' };
const MARKDOWN: Previewability = { kind: 'markdown' };
/** Rendered as a page by `HtmlPreview`, not as source by `CodePreview`. */
const HTML: Previewability = { kind: 'html' };
const BINARY: Previewability = { kind: 'binary' };

/**
 * Extension (without the leading dot) -> what that extension means.
 *
 * Code entries carry the extension as their CodeMirror language id, kept in
 * sync with `@codemirror/language-data`'s naming so `CodePreview` can resolve an
 * `Extension` via `languages.find(l => l.extensions.includes(ext))`.
 */
const CODE_EXTENSIONS = [
  'js',
  'jsx',
  'mjs',
  'cjs',
  'ts',
  'tsx',
  'py',
  'go',
  'rs',
  'java',
  'kt',
  'c',
  'h',
  'cpp',
  'hpp',
  'cs',
  'rb',
  'php',
  'sh',
  'bash',
  'sql',
  'json',
  'yaml',
  'yml',
  'toml',
  'xml',
  'css',
  'scss',
  'less',
  'graphql',
  'swift',
  'lua',
  'dockerfile',
  'vue',
];

/**
 * Rendered by the browser from a plain URL, so no content fetch is involved.
 * SVG is included: an `<img>` context does not execute script, so an uploaded
 * SVG cannot run anything the way an inlined one could.
 */
const IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'avif',
  'bmp',
  'ico',
];

const BY_EXTENSION = new Map<string, Classification>([
  ...(['md', 'markdown'] as const).map(
    (ext) => [ext, { label: 'Document', previewability: MARKDOWN }] as const,
  ),
  // HTML is a page, not source. "Routed ahead of code" holds *structurally* —
  // `html` and `htm` each appear exactly once in this table, so no `Map` spread
  // order decides the answer. Restoring `'html'` to `CODE_EXTENSIONS` would
  // reintroduce the bug silently, depending only on which spread came last.
  ...(['html', 'htm'] as const).map(
    (ext) => [ext, { label: 'Document', previewability: HTML }] as const,
  ),
  ...CODE_EXTENSIONS.map(
    (ext) =>
      [
        ext,
        { label: 'Code', previewability: { kind: 'code', language: ext } },
      ] as const,
  ),
  ...IMAGE_EXTENSIONS.map(
    (ext) => [ext, { label: 'Image', previewability: IMAGE }] as const,
  ),
  // `csv` is a spreadsheet to a reader and plain text to the preview pane. Both
  // come from this one entry, so the two answers cannot disagree even though
  // they differ.
  ['csv', { label: 'Spreadsheet', previewability: TEXT }],
  ...(['txt', 'log', 'ini', 'cfg', 'conf', 'env'] as const).map(
    (ext) => [ext, { label: 'Text', previewability: TEXT }] as const,
  ),
  ['pdf', { label: 'PDF', previewability: PDF }],
  ...(['xlsx', 'xls', 'ods'] as const).map(
    (ext) => [ext, { label: 'Spreadsheet', previewability: BINARY }] as const,
  ),
  ...(['doc', 'docx', 'odt', 'rtf', 'pages'] as const).map(
    (ext) => [ext, { label: 'Document', previewability: BINARY }] as const,
  ),
  ...(['zip', 'tar', 'gz', 'tgz', 'bz2', 'xz', '7z', 'rar'] as const).map(
    (ext) => [ext, { label: 'Archive', previewability: BINARY }] as const,
  ),
  ...(['mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac'] as const).map(
    (ext) => [ext, { label: 'Audio', previewability: BINARY }] as const,
  ),
  ...(['mp4', 'mov', 'webm', 'mkv', 'avi'] as const).map(
    (ext) => [ext, { label: 'Video', previewability: BINARY }] as const,
  ),
]);

/** Structured, text-ish `content_type` values that are not `text/*`. */
const TEXT_LIKE_CONTENT_TYPES = new Set([
  'application/json',
  'application/xml',
  'application/x-yaml',
  'application/toml',
]);

const SPREADSHEET_CONTENT_TYPES = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
]);

const ARCHIVE_CONTENT_TYPES = new Set([
  'application/zip',
  'application/x-tar',
  'application/gzip',
  'application/x-7z-compressed',
]);

function getExtension(name: string): string | undefined {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === name.length - 1) return undefined;
  return name.slice(lastDot + 1).toLowerCase();
}

/**
 * The bare media type of a `Content-Type`: parameters dropped, lower-cased,
 * trimmed.
 *
 * `text/html; charset=utf-8` and `TEXT/HTML` name the same type as `text/html`,
 * and a classifier comparing the whole header string answers differently for
 * each. Every comparison in `fromContentType` goes through this: the whole-type
 * branches need it to match at all, and the `startsWith` prefix branches —
 * tolerant of trailing parameters but not of case — use it so that `IMAGE/PNG`
 * is an image too. One normalisation, no per-branch exceptions to remember.
 */
function mediaTypeOf(contentType: string): string {
  const semicolon = contentType.indexOf(';');
  return (semicolon === -1 ? contentType : contentType.slice(0, semicolon))
    .trim()
    .toLowerCase();
}

function fromContentType(contentType: string): Classification | undefined {
  const mediaType = mediaTypeOf(contentType);
  if (mediaType.startsWith('image/')) {
    return { label: 'Image', previewability: IMAGE };
  }
  if (mediaType.startsWith('audio/')) {
    return { label: 'Audio', previewability: BINARY };
  }
  if (mediaType.startsWith('video/')) {
    return { label: 'Video', previewability: BINARY };
  }
  if (mediaType === 'application/pdf') {
    return { label: 'PDF', previewability: PDF };
  }
  if (SPREADSHEET_CONTENT_TYPES.has(mediaType)) {
    return { label: 'Spreadsheet', previewability: BINARY };
  }
  if (ARCHIVE_CONTENT_TYPES.has(mediaType)) {
    return { label: 'Archive', previewability: BINARY };
  }
  // Ahead of the `text/*` branch: a file named without a usable extension but
  // declared as HTML is a page, not plain text (FR-005).
  //
  // Matched on the *media type* with its parameters stripped, because the
  // ordinary form of this header is `text/html; charset=utf-8` and an
  // exact-equality compare missed it — the markup was then dumped as plain
  // text with nothing saying it had been misclassified (spec 140 FR-072).
  if (mediaType === 'text/html') {
    return { label: 'Document', previewability: HTML };
  }
  if (mediaType.startsWith('text/') || TEXT_LIKE_CONTENT_TYPES.has(mediaType)) {
    return { label: 'Text', previewability: TEXT };
  }
  return undefined;
}

/**
 * What this entry is: the Kind shown in the listing and how it can be previewed.
 *
 * A directory short-circuits everything — it has no extension worth reading and
 * no content to preview.
 */
export function classifyFileEntry(entry: FileEntry): Classification {
  if (entry.kind === 'directory') {
    return { label: 'Folder', previewability: BINARY };
  }

  const extension = getExtension(entry.name);
  const byExtension =
    extension === undefined ? undefined : BY_EXTENSION.get(extension);
  if (byExtension !== undefined) return byExtension;

  const byContentType = fromContentType(entry.content_type ?? '');
  if (byContentType !== undefined) return byContentType;

  // Neither signal identified it. `File` rather than a blank cell (FR-003).
  return { label: 'File', previewability: BINARY };
}
